export type ArrayElementFunction<T, S> = (
    element: T,
    index: number,
    array: T[]
) => S;

export type ReadonlyArrayElementFunction<T, S> = (
    element: T,
    index: number,
    array: ReadonlyArray<T>
) => S;

export type CompareFunction<T> = (a: T, b: T) => number;

/**
 * Splits apart an array into parts of a given size.
 * @example
 * ```
 * arrayChunks([1, 2, 3, 4, 5, 6, 7, 8, 9], 3)
 * // returns [[1, 2, 3], [4, 5, 6], [7, 8, 9]]
 * ```
 * @param array The array to split apart.
 * @param size The size of each chunk.
 * @returns An array of arrays, each of which is a chunk of the original array.
 * */
export function arrayChunks<T>(array: ReadonlyArray<T>, size: number): T[][] {
    // TODO use adhublib arrayutils.chunk
    if (size === Infinity) {
        // +1 is a simple guard against empty input arrays. If we just used
        // the length it'd fail the 0 check
        size = array.length + 1;
    }

    if (!Number.isSafeInteger(size) || size < 1) {
        throw new Error('size must be a positive integer or +Infinity');
    }

    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
}

/**
 * Combination map and filter in a single Array pass.
 *
 * Produces a new Array by applying a function to each element of the input
 * Array, and including the result in the output Array if the result is not
 * undefined.
 */
export function arrayMapFilter<V, R>(
    array: V[],
    callbackfn: ArrayElementFunction<V, R | undefined>
): R[] {
    const result: R[] = [];
    for (let i = 0; i < array.length; i++) {
        const mapped = callbackfn(array[i], i, array);
        if (mapped !== undefined) result.push(mapped);
    }
    return result;
}

/**
 * Produces a new Array by including only the elements of the input Array
 * for which the predicate function returns false.
 *
 * This is like `Array.filter`, but with the meaning of the predicate
 * inverted.
 */
export function arrayReject<T>(array: T[], condition: (item: T) => boolean) {
    return array.filter((item) => !condition(item));
}

/**
 * Modifies the given Array in-place, deleting all elements for which the
 * predicate function returns true.
 *
 * This is like `arrayReject`, but it modifies the input Array instead of
 * returning a new one.
 */
export function arrayInPlaceReject<T>(
    array: T[],
    condition: (item: T) => boolean
) {
    for (let i = array.length - 1; i >= 0; i--) {
        if (condition(array[i])) {
            array.splice(i, 1);
        }
    }
}

/**
 * Splits an array into two arrays using a predicate - the first containing
 * the items for which the predicate returns true, and the second
 * containing the rest.
 */
export function arrayFilterReject<T>(
    array: T[],
    predicate: ArrayElementFunction<T, boolean>,
    predicateThisArg: any = undefined
): [T[], T[]] {
    // this is basically a copy of the one in adhublib
    const trues: T[] = [];
    const falses: T[] = [];

    for (let i = 0; i < array.length; i++) {
        const result = predicate.call(predicateThisArg, array[i], i, array);
        (result ? trues : falses).push(array[i]);
    }

    return [trues, falses];
}

export function arrayCount<T>(
    array: ReadonlyArray<T>,
    predicate: ReadonlyArrayElementFunction<T, boolean>
): number {
    let count = 0;
    for (let i = 0; i < array.length; i++) {
        if (predicate(array[i], i, array)) {
            count++;
        }
    }
    return count;
}

export function last<T>(array: ReadonlyArray<T>): T | undefined {
    return array.length > 0 ? array[array.length - 1] : undefined;
}

export function arrayRemoveFirstMatching<T>(
    array: T[],
    predicate: (item: T) => boolean
): T | undefined {
    const index = array.findIndex(predicate);
    if (index >= 0) {
        return array.splice(index, 1)[0];
    }
    return undefined;
}

function arrayExtremaBy<T>(
    array: ReadonlyArray<T>,
    compareFn: CompareFunction<T>,
    wantsMax: boolean
): T | undefined {
    if (array.length === 0) return undefined;
    let extremaElement = array[0];
    for (let i = 1; i < array.length; i++) {
        const comparison = compareFn(array[i], extremaElement);
        if ((wantsMax && comparison > 0) || (!wantsMax && comparison < 0)) {
            extremaElement = array[i];
        }
    }
    return extremaElement;
}

export function arrayMaxBy<T>(
    array: ReadonlyArray<T>,
    compareFn: CompareFunction<T>
): T | undefined {
    return arrayExtremaBy(array, compareFn, true);
}

export function arrayMinBy<T>(
    array: ReadonlyArray<T>,
    compareFn: CompareFunction<T>
): T | undefined {
    return arrayExtremaBy(array, compareFn, false);
}
